import appConstants from "../../../../../../appConstants";
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter
} from '@dataGrid/components/columnFilters';
import {
  SpecialNeedsValueGetter,
  SpecialNeedsRenderer,
} from '@dataGrid/components/cellRenderer';
import {
  customAlphanumericComparator,
  customNumericComparator,
  formatDate,
  formatDeliveryWindow,
  isValidTimeValue,
  mergeUserConfig,
} from '@dataGrid/gridUtilities';
import { COLUMN_CONFIG } from "../../../../IBPlanning/ShipmentsView/constant";
import { CustomerNameCellRenderer } from "../../../../../common/CustomerMergeIndicator/CustomerNameCell";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({ columnConfig, userConfig }) {
    const defaultConfig = GridConfig.getDefaultConfiguration({ columnConfig });
    return mergeUserConfig({
      defaultConfig,
      userConfig
    });
  }
    
  static getProNumberClasses(params) {
    return [
      "gridCell-leftAlign",
      params.context.hasProImagesURL ? "actionable-cell" : "",
    ];
  }


  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getAPTWindowConfig(columnConfig) {
    if (columnConfig === COLUMN_CONFIG.SAIA) {
      return {
        headerName: "Delivery Window",
        headerTooltip: "Delivery Window",
        valueGetter: (params) =>
          params.data.shipmentCTA || params.data.deliveryWindow.join(","),
        valueFormatter: formatDeliveryWindow,
      };
    }
    return {
      headerName: "APT Window",
      headerTooltip: "Appointment Window",
      valueGetter: (params) => params.data.shipmentCTA,
      valueFormatter: (params) => {
        if (!isValidTimeValue(params.value)) {
          return;
        }
        return params.value;
      },
    };
  }

  static getDefaultConfiguration({ columnConfig }) {
    const aptConfig = GridConfig.getAPTWindowConfig(columnConfig);
    const defaultConfig = {
      title: appConstants.gridNames.TrailerShipmentsGrid,
      keyColumn: "proId",
      rowSelection: "multiple",
      rowDeselection: true,
      columns: [
        {
          headerName: "Route",
          headerTooltip: "Route Name",
          headerClass: "routeNameCell",
          field: "routeName",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_80,
          minWidth: appConstants.columnWidth.W_120,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Seq",
          field: "stopSequence",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_30,
          headerTooltip: "Stop Sequence",
          minWidth: appConstants.columnWidth.STOP_SEQUENCE,
          maxWidth: appConstants.columnWidth.STOP_SEQUENCE,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Pro#",
          field: "proId",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_80,
          headerTooltip: "Pro Number",
          minWidth: appConstants.columnWidth.PROID,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: appConstants.FILTER_TYPES.STRING,
          },
          cellClass: GridConfig.getProNumberClasses,
        },
        {
          headerName: "Customer",
          field: "displayCustomerName",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_150,
          headerTooltip: "Customer Name",
          minWidth: appConstants.columnWidth.CUSTOMER_NAME,
          pinned: "left",
          lockPinned: true,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          cellRenderer: CustomerNameCellRenderer,
          filterParams:{
            width: appConstants.columnWidth.W_300
          }
        },
        {
          headerName: "Priority",
          field: "priority",
          headerTooltip: "Priority Code",
          minWidth: appConstants.columnWidth.W_80,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Spl Needs",
          headerTooltip: "Special Needs",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_100,
          minWidth: appConstants.columnWidth.SPL_NEED,
          cellClass: "gridCell-leftAlign",
          valueGetter: SpecialNeedsValueGetter,
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          cellRendererParams: {
            getAPTWindow: aptConfig.valueGetter,
          },
          cellRenderer: SpecialNeedsRenderer,
        },
        {
          headerName: "Start Time",
          field: "routeStartTime",
          headerTooltip: "Route Start Time",
          minWidth: appConstants.columnWidth.START_TIME,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
          },
        },
        {
          headerName: "Ldg Zone",
          field: "loadingZone",
          headerTooltip: "Loading Zone",
          minWidth: appConstants.columnWidth.LDG_ZONE,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: aptConfig.headerName,
          headerTooltip: aptConfig.headerTooltip,
          field: "shipmentCTA",
          minWidth: appConstants.columnWidth.EARLY_LATE_CTA,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
          valueGetter: aptConfig.valueGetter,
          valueFormatter: aptConfig.valueFormatter,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "Delivery Window",
          headerTooltip: "Delivery Window",
          field: "deliveryWindow",
          minWidth: appConstants.columnWidth.DELIVERY_WINDOW,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
          editable: false,
          valueFormatter: formatDeliveryWindow,
        },
        {
          headerName: "City",
          headerTooltip: "City",
          field: "city",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_80,
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.CITY,
          filter: MultiSelectFilter,
          tooltipField: "city",
        },
        {
          headerName: "Zip",
          headerTooltip: "Zip",
          field: "zip",
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.ZIP_CODE,
          filter: MultiSelectFilter,
        },
        {
          headerName: "IB Trlr #",
          headerTooltip: "Inbound Trailer Number",
          field: "trailerNumber",
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.W_110,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: appConstants.FILTER_TYPES.STRING,
          },
        },
        {
          headerName: "Door",
          headerTooltip: "Route Planning Door",
          field: "routeDoor",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_30,
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.W_80,
          filter: MultiSelectFilter,
          comparator: customNumericComparator,
        },
        {
          headerName: "Trailer",
          headerTooltip: "Route Planning Trailer",
          field: "routeTrailerNumber",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_50,
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.W_110,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: appConstants.FILTER_TYPES.STRING,
          },
        },
        {
          headerName: "Pcs",
          headerTooltip: "Pieces",
          field: "pieces",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_30,
          cellClass: "gridCell-centerAlign",
          minWidth: appConstants.columnWidth.W_80,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Weight",
          headerTooltip: "Weight",
          field: "weight",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_50,
          cellClass: "gridCell-centerAlign",
          minWidth: appConstants.columnWidth.WEIGHT,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Temp",
          headerTooltip: "Temperature Type",
          field: "tempType",
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.W_80,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Due Date",
          headerTooltip: "Due Date",
          field: "dueDate",
          isPrintable: true,
          printColWidth: appConstants.columnWidth.W_80,
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.DUE_DATE,
          valueFormatter: formatDate,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.DATE,
          },
        },
        {
          headerName: "Bay Location",
          headerTooltip: "Bay Location",
          field: "bayLocation",
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.W_130,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: appConstants.FILTER_TYPES.STRING,
          },
          comparator: customAlphanumericComparator,
        },
        {
          headerName: "Shipment note",
          headerTooltip: "Shipment note",
          field: "shipmentComments",
          isPrintable: true,
          printHeaderName: "Notes",
          printColWidth: appConstants.columnWidth.W_120,
          cellClass: "gridCell-leftAlign",
          minWidth: appConstants.columnWidth.W_130,
          filter: MultiSelectFilter,
          tooltipField: "shipmentComments",
        },
      ],
    };
    if (columnConfig === COLUMN_CONFIG.SAIA) {
      defaultConfig.columns = defaultConfig.columns.filter(
        (column) => column.field !== "deliveryWindow"
      );
    }
    return defaultConfig;
  }
}
